import { showErrorNotification, showInfoNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { PricingAction } from '../constants'
import type { PricingControlEmits, PricingControlState, PricingDecision } from '../types'
import { asBuyingOptionId, asPartId, asSellingOptionId } from '../types'
import { useBillPartPricingApi } from './useBillPartPricingApi'
import { useBillPartPricingValidation } from './useBillPartPricingValidation'

export function buildPricingDecision(item: PricingControlState['items'][number]): PricingDecision {
  const transactionItemId = item.transactionItemId

  switch (item.action) {
    case PricingAction.UPDATE:
      if (!item.partId || !item.sellingOptionId) {
        throw new Error('An existing part and selling option are required to update a price')
      }

      return {
        action: PricingAction.UPDATE,
        transaction_item_id: transactionItemId,
        part_id: item.partId,
        selling_option_id: item.sellingOptionId,
        new_price: item.proposedPrice,
      }
    case PricingAction.CREATE_NEW_PART:
      if (item.partId) {
        throw new Error('A new part pricing decision cannot reference an existing part')
      }

      return {
        action: PricingAction.CREATE_NEW_PART,
        transaction_item_id: transactionItemId,
        part_id: null,
        new_price: item.proposedPrice,
      }
    case PricingAction.CREATE_SELLING_OPTION_LINKED: {
      if (!item.partId) {
        throw new Error('An existing part is required to create a linked selling option')
      }

      const buyingOptionId = item.availableBuyingOptions?.[0]?.id
      if (!buyingOptionId) {
        throw new Error('A buying option is required to create a linked selling option')
      }

      return {
        action: PricingAction.CREATE_SELLING_OPTION_LINKED,
        transaction_item_id: transactionItemId,
        part_id: item.partId,
        buying_option_id: buyingOptionId,
        option_type: 'linked',
        new_price: item.proposedPrice,
      }
    }
    case PricingAction.CREATE_SELLING_OPTION_INDEPENDENT:
      if (!item.partId) {
        throw new Error('An existing part is required to create an independent selling option')
      }

      return {
        action: PricingAction.CREATE_SELLING_OPTION_INDEPENDENT,
        transaction_item_id: transactionItemId,
        part_id: item.partId,
        option_type: 'independent',
        new_price: item.proposedPrice,
      }
    case PricingAction.KEEP:
    case PricingAction.SKIP:
      if (!item.partId) {
        return {
          action: item.action,
          transaction_item_id: transactionItemId,
          part_id: null,
        }
      }

      return {
        action: item.action,
        transaction_item_id: transactionItemId,
        part_id: item.partId,
        ...(item.sellingOptionId ? { selling_option_id: item.sellingOptionId } : {}),
      }
    case PricingAction.PENDING:
      throw new Error('A pending pricing decision cannot be submitted')
  }
}

export function useBillPartPricingLogic(state: Ref<PricingControlState>, emit: PricingControlEmits, transactionId?: string) {
  const { t } = useI18n()
  const api = useBillPartPricingApi(state)
  const validation = useBillPartPricingValidation(state)

  const setItemAction = (itemId: string, action: PricingAction) => {
    const index = state.value.items.findIndex((item) => item.id === itemId)
    if (index !== -1) {
      state.value.items[index].action = action
      state.value.hasUnsavedChanges = true
      emit('change', true)
    }
  }

  const applyPricingDecisions = async (): Promise<void> => {
    if (state.value.items.length === 0) {
      emit('error', t('composites.BillPartPricingControl.errors.noItems'))
      return
    }

    // Validate before applying
    const validationResult = validation.validateBeforeApply()
    if (!validationResult.canApply) {
      emit('error', validationResult.errors.join(', '))
      return
    }

    try {
      state.value.isProcessing = true

      const decisions: PricingDecision[] = state.value.items
        .filter((item) => item.action !== PricingAction.SKIP)
        .map(buildPricingDecision)

      // Apply decisions via API
      if (!transactionId) {
        throw new Error('Transaction ID is required to apply pricing decisions')
      }
      const result = await api.applyPricingDecisions(transactionId, decisions)

      // Handle both response formats: { success, updated_count } or { data, message, finalization }
      const isSuccess = result.success === true || result.data === true || result.finalization?.success === true

      if (isSuccess) {
        state.value.hasUnsavedChanges = false

        // Use the message from the backend if available, otherwise use the default translation
        const successMessage =
          result.message ||
          t('composites.BillPartPricingControl.success.decisionsApplied', {
            count: result.updated_count || decisions.length,
          })

        showSuccessNotification(successMessage)

        emit('complete', decisions)
      } else {
        throw new Error(result.errors?.join(', ') || t('composites.BillPartPricingControl.errors.applyFailed'))
      }
    } catch (error) {
      console.error('Failed to apply pricing decisions:', error)
      const errorMessage = error instanceof Error ? error.message : t('composites.BillPartPricingControl.errors.applyFailed')

      showErrorNotification(errorMessage, error)

      emit('error', errorMessage)
    } finally {
      state.value.isProcessing = false
    }
  }

  const resetChanges = () => {
    // Reset all items to pending state
    state.value.items.forEach((item) => {
      item.action = PricingAction.PENDING
    })

    state.value.hasUnsavedChanges = false
    state.value.error = null

    emit('change', false)

    showInfoNotification(t('composites.BillPartPricingControl.success.changesReset'))
  }

  const loadPricingAnalysis = async (transactionId: string): Promise<void> => {
    try {
      await api.loadPricingAnalysis(transactionId)
      emit('ready')
    } catch (error) {
      console.error('Failed to load pricing analysis:', error)
      const errorMessage = error instanceof Error ? error.message : t('composites.BillPartPricingControl.errors.loadFailed')
      emit('error', errorMessage)
    }
  }

  const validatePricingDecisions = (): string[] => {
    // Use the comprehensive validation system
    const validationResult = validation.validateBeforeApply()
    return validationResult.errors
  }

  const createLinkedSellingOption = async (itemId: string, buyingOptionId: string): Promise<void> => {
    const item = state.value.items.find((i) => i.id === itemId)
    if (!item || !item.partId) {
      throw new Error('Item or part not found')
    }

    try {
      state.value.isProcessing = true

      const sellingOptionData = {
        part_id: item.partId,
        buying_option_id: asBuyingOptionId(buyingOptionId),
        option_type: 'linked' as const,
        name: `${item.name} - Linked`,
        price: item.proposedPrice,
        part_number: item.oem || item.sku || '',
      }

      const result = await api.createSellingOption(sellingOptionData)

      if (result.success) {
        // Update the item with the new selling option
        item.action = PricingAction.UPDATE
        item.sellingOptionId = result.selling_option_id ? asSellingOptionId(result.selling_option_id) : null
        state.value.hasUnsavedChanges = true
        emit('change', true)

        showSuccessNotification(t('composites.BillPartPricingControl.success.sellingOptionCreated'))
      }
    } catch (error) {
      console.error('Failed to create linked selling option:', error)
      showErrorNotification(error instanceof Error ? error.message : t('composites.BillPartPricingControl.errors.createSellingOptionFailed'), error)
    } finally {
      state.value.isProcessing = false
    }
  }

  const createIndependentSellingOption = async (itemId: string): Promise<void> => {
    const item = state.value.items.find((i) => i.id === itemId)
    if (!item || !item.partId) {
      throw new Error('Item or part not found')
    }

    try {
      state.value.isProcessing = true

      const sellingOptionData = {
        part_id: item.partId,
        option_type: 'independent' as const,
        name: `${item.name} - Independent`,
        price: item.proposedPrice,
        part_number: item.oem || item.sku || '',
      }

      const result = await api.createSellingOption(sellingOptionData)

      if (result.success) {
        // Update the item with the new selling option
        item.action = PricingAction.UPDATE
        item.sellingOptionId = result.selling_option_id ? asSellingOptionId(result.selling_option_id) : null
        state.value.hasUnsavedChanges = true
        emit('change', true)

        showSuccessNotification(t('composites.BillPartPricingControl.success.sellingOptionCreated'))
      }
    } catch (error) {
      console.error('Failed to create independent selling option:', error)
      showErrorNotification(error instanceof Error ? error.message : t('composites.BillPartPricingControl.errors.createSellingOptionFailed'), error)
    } finally {
      state.value.isProcessing = false
    }
  }

  const createNewPartFromItem = async (itemId: string): Promise<void> => {
    const item = state.value.items.find((i) => i.id === itemId)
    if (!item) {
      throw new Error('Item not found')
    }

    try {
      state.value.isProcessing = true

      const partData = {
        name: item.name,
        sku: item.sku || '',
        oem: item.oem || '',
        description: `Created from transaction item`,
        selling_price: item.proposedPrice,
      }

      const result = await api.createNewPart(partData)

      if (result.success) {
        // Update the item to reference the new part
        item.partId = result.part_id ? asPartId(result.part_id) : null
        item.action = PricingAction.UPDATE
        item.isNewPart = false
        state.value.hasUnsavedChanges = true
        emit('change', true)

        showSuccessNotification(t('composites.BillPartPricingControl.success.partCreated'))
      }
    } catch (error) {
      console.error('Failed to create new part:', error)
      showErrorNotification(error instanceof Error ? error.message : t('composites.BillPartPricingControl.errors.createPartFailed'))
    } finally {
      state.value.isProcessing = false
    }
  }

  return {
    setItemAction,
    applyPricingDecisions,
    resetChanges,
    loadPricingAnalysis,
    validatePricingDecisions,
    createLinkedSellingOption,
    createIndependentSellingOption,
    createNewPartFromItem,

    // Validation methods
    validation,
  }
}
